(function () {
  const PREFETCH_LIMIT = 18;
  const PRERENDER_LIMIT = 2;
  const INITIAL_PREFETCH_LIMIT = 8;
  const PREFETCH_CONCURRENCY = 2;
  const HOVER_DELAY_MS = 70;
  const FETCH_TIMEOUT_MS = 5000;

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const saveDataEnabled = connection?.saveData === true;
  const effectiveConnection = String(connection?.effectiveType || '').toLowerCase();
  const isSlowConnection = effectiveConnection.includes('2g');
  const isLowPowerDevice = Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  const isDesignMode = window.Shopify?.designMode === true;

  if (saveDataEnabled || isSlowConnection || isLowPowerDevice || isDesignMode) return;

  const supportsSpeculationRules =
    typeof HTMLScriptElement !== 'undefined' &&
    typeof HTMLScriptElement.supports === 'function' &&
    HTMLScriptElement.supports('speculationrules');

  const warmed = new Set();
  const queued = new Set();
  const prefetchHints = new Set();
  const queue = [];
  let inflight = 0;

  let hoverTimer = 0;
  let lastHoveredAnchor = null;

  const speculationPrefetch = [];
  const speculationPrefetchSet = new Set();
  const speculationPrerender = [];
  const speculationPrerenderSet = new Set();
  let speculationScript = null;

  const SKIP_PATHNAME_RE = /^\/(?:cart|checkout|checkouts|challenge|apps\/)/i;
  const FILE_EXTENSION_RE = /\.(?:pdf|zip|rar|7z|mp4|webm|mov|mp3|wav|xml|json|txt|csv|jpe?g|png|gif|webp|svg)$/i;

  function normalizeAnchorURL(anchor) {
    if (!(anchor instanceof HTMLAnchorElement)) return null;
    if (!anchor.href || anchor.getAttribute('href')?.startsWith('#')) return null;
    if (anchor.target && anchor.target !== '_self') return null;
    if (anchor.hasAttribute('download')) return null;
    if (anchor.dataset.noPrefetch === 'true') return null;

    const rel = String(anchor.getAttribute('rel') || '').toLowerCase();
    if (rel.includes('external')) return null;

    let url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch (_error) {
      return null;
    }

    if (url.origin !== window.location.origin) return null;
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (SKIP_PATHNAME_RE.test(url.pathname)) return null;
    if (FILE_EXTENSION_RE.test(url.pathname)) return null;

    const normalized = new URL(url.toString());
    normalized.hash = '';

    if (
      normalized.pathname === window.location.pathname &&
      normalized.search === window.location.search
    ) {
      return null;
    }

    return normalized.toString();
  }

  function queueURL(url, options = {}) {
    const allowPrerender = options.allowPrerender === true;
    if (!url) return;
    if (warmed.has(url) || queued.has(url)) return;
    if (warmed.size + queued.size >= PREFETCH_LIMIT) return;

    queued.add(url);
    queue.push({ url, allowPrerender });
    flushQueue();
  }

  function queueFromTarget(target, options = {}) {
    if (!(target instanceof Element)) return;
    const anchor = target.closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) return;
    const normalizedURL = normalizeAnchorURL(anchor);
    queueURL(normalizedURL, options);
  }

  function flushQueue() {
    while (inflight < PREFETCH_CONCURRENCY && queue.length > 0) {
      const entry = queue.shift();
      if (!entry) break;

      queued.delete(entry.url);
      inflight += 1;

      warmURL(entry.url, entry.allowPrerender)
        .catch(() => {})
        .finally(() => {
          inflight -= 1;
          flushQueue();
        });
    }
  }

  function warmURL(url, allowPrerender) {
    if (warmed.has(url)) return Promise.resolve();
    warmed.add(url);

    if (supportsSpeculationRules) {
      registerSpeculationURL(url, allowPrerender);
      return Promise.resolve();
    }

    addPrefetchHint(url);
    return warmViaFetch(url);
  }

  function addPrefetchHint(url) {
    if (prefetchHints.has(url)) return;
    prefetchHints.add(url);

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'document';
    link.href = url;
    document.head.appendChild(link);
  }

  function warmViaFetch(url) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timeoutId = controller
      ? window.setTimeout(() => {
          controller.abort();
        }, FETCH_TIMEOUT_MS)
      : 0;

    return fetch(url, {
      credentials: 'same-origin',
      cache: 'force-cache',
      signal: controller ? controller.signal : undefined,
    })
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        if (timeoutId) window.clearTimeout(timeoutId);
      });
  }

  function registerSpeculationURL(url, allowPrerender) {
    if (allowPrerender && speculationPrerender.length < PRERENDER_LIMIT) {
      if (!speculationPrerenderSet.has(url)) {
        speculationPrerenderSet.add(url);
        speculationPrerender.push(url);
      }
    } else if (!speculationPrefetchSet.has(url)) {
      speculationPrefetchSet.add(url);
      speculationPrefetch.push(url);
    }

    applySpeculationRules();
  }

  function applySpeculationRules() {
    if (!supportsSpeculationRules) return;
    if (!(speculationScript instanceof HTMLScriptElement)) {
      speculationScript = document.createElement('script');
      speculationScript.type = 'speculationrules';
      document.head.appendChild(speculationScript);
    }

    const rules = {};

    if (speculationPrerender.length) {
      rules.prerender = [{ source: 'list', urls: speculationPrerender.slice(0, PRERENDER_LIMIT) }];
    }

    if (speculationPrefetch.length) {
      rules.prefetch = [{ source: 'list', urls: speculationPrefetch.slice(0, PREFETCH_LIMIT) }];
    }

    speculationScript.textContent = JSON.stringify(rules);
  }

  function prefetchInitialLinks() {
    const selectors = [
      'main a[href*="/products/"]',
      'main a[href*="/collections/"]',
      'header a[href]',
      'main a[href]',
    ];
    const selected = new Set();

    for (const selector of selectors) {
      const anchors = document.querySelectorAll(selector);
      for (const anchor of anchors) {
        const url = normalizeAnchorURL(anchor);
        if (!url || selected.has(url)) continue;
        selected.add(url);
        queueURL(url, { allowPrerender: false });

        if (selected.size >= INITIAL_PREFETCH_LIMIT) return;
      }
    }
  }

  function scheduleInitialPrefetch() {
    const run = () => prefetchInitialLinks();

    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 1500 });
    } else {
      setTimeout(run, 500);
    }
  }

  document.addEventListener(
    'pointerover',
    (event) => {
      if (!(event instanceof PointerEvent) || event.pointerType !== 'mouse') return;
      if (!(event.target instanceof Element)) return;

      const anchor = event.target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor === lastHoveredAnchor) return;

      lastHoveredAnchor = anchor;
      window.clearTimeout(hoverTimer);
      hoverTimer = window.setTimeout(() => {
        const url = normalizeAnchorURL(anchor);
        queueURL(url, { allowPrerender: true });
      }, HOVER_DELAY_MS);
    },
    { passive: true }
  );

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (event instanceof PointerEvent && event.button !== 0) return;
      queueFromTarget(event.target, { allowPrerender: true });
    },
    { passive: true }
  );

  document.addEventListener('focusin', (event) => {
    queueFromTarget(event.target, { allowPrerender: true });
  });

  document.addEventListener(
    'touchstart',
    (event) => {
      queueFromTarget(event.target, { allowPrerender: true });
    },
    { passive: true }
  );

  document.addEventListener('theme:prefetch-document', (event) => {
    const customEvent = /** @type {CustomEvent} */ (event);
    const urlValue = customEvent?.detail?.url;
    if (typeof urlValue !== 'string' || !urlValue) return;

    let url;
    try {
      url = new URL(urlValue, window.location.href).toString();
    } catch (_error) {
      return;
    }

    queueURL(url, { allowPrerender: customEvent?.detail?.allowPrerender === true });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInitialPrefetch, { once: true });
  } else {
    scheduleInitialPrefetch();
  }
})();
